import type { LangCode } from "@/api/types";

import { EditableSection } from "./EditableSection";
import { Field } from "./Field";
import type { RecordTabFormProps } from "./shared";

const ALIAS_KINDS = [
  "inflection",
  "synonym",
  "orthographic",
  "transliteration",
  "abbreviation",
  "forbidden",
];

/** Форма для item_type=term_alias и alias_candidate.
 *  Алиас ВСЕГДА в одном языке с canonical'ом — никаких двух колонок
 *  EN/RU как у glossary_term'а. Reviewer редактирует alias_text и
 *  alias_kind, canonical readonly. */
export function TermAliasForm({
  item,
  payload,
  editMode,
  draft,
  onChange,
  onCancelEdit,
}: RecordTabFormProps) {
  const aliasLang = (payload.alias_lang ?? item.target_lang ?? null) as
    | LangCode
    | null;

  const aliasTextValue =
    editMode && draft.alias_text != null
      ? draft.alias_text
      : (payload.alias_text ?? payload.alias ?? item.candidate_text ?? "");
  const aliasKindValue =
    editMode && draft.alias_kind != null
      ? draft.alias_kind
      : (payload.alias_kind ?? "");

  return (
    <>
      <CanonicalRef payload={payload} />

      <EditableSection
        title="Альтернативная форма"
        editMode={editMode}
        draftCount={Object.keys(draft).length}
        onCancelEdit={onCancelEdit}
      >
        <div className="rf-form-grid">
          <Field
            fieldKey="alias_text"
            label="Текст альтернативы"
            langTag={aliasLang}
            value={aliasTextValue}
            editable={editMode}
            keepEmpty
            onChange={onChange}
          />
          {editMode ? (
            <div className="mt-field">
              <span className="mt-field__label">Тип альтернативы</span>
              <select
                className="mt-input"
                value={aliasKindValue}
                onChange={(e) => onChange("alias_kind", e.target.value)}
              >
                <option value="">— не выбрано —</option>
                {ALIAS_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <Field
              label="Тип альтернативы"
              value={aliasKindValue}
              editable={false}
            />
          )}
        </div>
      </EditableSection>
    </>
  );
}

function CanonicalRef({ payload }: { payload: RecordTabFormProps["payload"] }) {
  const canonical = payload.canonical_text;
  const canonicalLang = payload.canonical_lang;
  const reason = payload.reason;
  const policy = payload.approval_policy;

  if (!canonical && !reason && !policy) return null;

  return (
    <section className="rf-section">
      <div className="rf-section__head">
        <span>Канонический термин</span>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <span
          className="mt-text-tight mt-muted"
          style={{ textTransform: "none", letterSpacing: 0 }}
        >
          readonly · алиас наследует значение от него
        </span>
      </div>
      <div className="rf-section__body">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(160px, max-content) 1fr",
            gap: "4px 12px",
            fontSize: "var(--fs-sm)",
          }}
        >
          {canonical && <Row k="Канон" v={canonical} />}
          {canonicalLang && <Row k="Язык канона" v={canonicalLang} />}
          {policy && <Row k="Политика" v={policy} />}
          {reason && <Row k="Причина" v={reason} />}
        </div>
      </div>
    </section>
  );
}

function Row({ k, v }: { k: string; v: unknown }) {
  return (
    <>
      <div className="mt-mono mt-text-tight mt-muted">{k}</div>
      <div style={{ overflowWrap: "anywhere" }}>{String(v)}</div>
    </>
  );
}
